import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ttMobileActionBtnSx,
  ttMobileAvatarSx,
  ttMobilePunchBlockSx,
  ttMobileSessionCardSx,
  ttSectionTitleSx,
} from "./timeTrackingStyles";
import { employeeInitials } from "./vacationCalendarUtils";

export function statusAccentFromMuiColor(theme, color) {
  const map = {
    warning: theme.palette.warning.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
    default: theme.palette.text.secondary,
  };
  return map[color] || theme.palette.primary.main;
}

export function statusTextColor(theme, color) {
  const map = {
    warning: theme.palette.warning.dark,
    success: theme.palette.success.dark,
    error: theme.palette.error.dark,
    info: theme.palette.info.dark,
    default: theme.palette.text.secondary,
  };
  return map[color] || theme.palette.primary.dark;
}

export function MobileRecordCard({
  theme,
  accent,
  avatarName,
  title,
  subtitle,
  statusLabel,
  statusColor = "default",
  footer,
  children,
}) {
  const barAccent = accent || statusAccentFromMuiColor(theme, statusColor);
  const chipColor = statusTextColor(theme, statusColor);

  return (
    <Box sx={ttMobileSessionCardSx(theme, barAccent)}>
      <Box sx={{ pl: 1.75, pr: 1.5, pt: 1.5, pb: footer ? 1.25 : 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: children ? 1.25 : 0 }}>
          {avatarName ? (
            <Box sx={ttMobileAvatarSx(theme, barAccent)}>
              {employeeInitials(avatarName)}
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title ? (
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography
                variant="body2"
                color={title ? "text.secondary" : "text.primary"}
                sx={{ fontWeight: title ? 600 : 800, lineHeight: 1.2 }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {statusLabel ? (
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                height: 24,
                fontWeight: 800,
                fontSize: "0.68rem",
                flexShrink: 0,
                bgcolor: alpha(barAccent, 0.14),
                border: "1px solid",
                borderColor: alpha(barAccent, 0.3),
                color: chipColor,
              }}
            />
          ) : null}
        </Stack>
        {children}
      </Box>
      {footer ? (
        <Box
          sx={{
            px: 1.5,
            py: 1.15,
            bgcolor: alpha(theme.palette.background.paper, 0.55),
            borderTop: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.45),
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}

export function MobileCardField({ label, children, sx }) {
  return (
    <Box sx={sx}>
      <Typography sx={{ ...ttSectionTitleSx, mb: 0.35 }}>{label}</Typography>
      {children}
    </Box>
  );
}

export function MobileCardMetaRow({ theme, children }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        p: 1,
        borderRadius: 1.75,
        bgcolor: alpha(theme.palette.background.paper, 0.65),
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.55),
      }}
    >
      {children}
    </Stack>
  );
}

export function MobileKpiBlock({ theme, label, value, color }) {
  const c = color || theme.palette.primary.main;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "center",
        py: 0.85,
        px: 0.5,
        borderRadius: 1.75,
        bgcolor: alpha(c, 0.08),
        border: "1px solid",
        borderColor: alpha(c, 0.2),
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: c, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function MobilePunchBlock({ theme, label, icon: Icon, color, children }) {
  return (
    <Box sx={ttMobilePunchBlockSx(theme, color)}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
        {Icon ? <Icon sx={{ fontSize: 14, color }} /> : null}
        <Typography sx={{ ...ttSectionTitleSx, mb: 0 }}>{label}</Typography>
      </Stack>
      {children}
    </Box>
  );
}

export function MobileActionGrid({ theme, actions }) {
  if (!actions?.length) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: actions.length > 1 ? "1fr 1fr" : "1fr",
        gap: 0.75,
      }}
    >
      {actions.map((action) => (
        <Button
          key={action.key}
          variant={action.variant || "outlined"}
          color={action.color || "primary"}
          size="small"
          startIcon={action.startIcon}
          onClick={action.onClick}
          sx={{
            ...ttMobileActionBtnSx,
            ...(action.variant === "contained"
              ? {}
              : {
                  borderColor: alpha(
                    theme.palette[action.color || "primary"]?.main ||
                      theme.palette.primary.main,
                    0.35,
                  ),
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                }),
          }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
}

export function MobileEmptyState({ children }) {
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography color="text.secondary">{children}</Typography>
    </Box>
  );
}
